/* Hai đường nhập giọng nói:
   · createVoice          — Web Speech API của trình duyệt (Chrome, Edge, Safari; ⛔ Firefox). Dùng khi KHÔNG có server
                            nhận dạng (backend cục bộ, server tắt STT).
   · createRecorderVoice  — ghi âm + nhận dạng ở server (mọi trình duyệt có micro, kể cả Firefox; tiếng Việt + từ vựng
                            ngành). Widget tự chuyển sang đường này khi phiên báo `voice.server`.
   Không đường nào dùng được thì nút mic ẩn hẳn thay vì đứng đó không làm gì. */

interface SpeechAlternative {
  transcript: string;
}
interface SpeechResult {
  0: SpeechAlternative;
  isFinal: boolean;
  length: number;
}
interface SpeechResultList {
  length: number;
  [index: number]: SpeechResult;
}
interface SpeechEvent {
  resultIndex: number;
  results: SpeechResultList;
}
interface SpeechErrorEvent {
  error: string;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechErrorEvent) => void) | null;
}
type SpeechCtor = new () => SpeechRecognitionLike;

export interface VoiceHandlers {
  onStart(): void;
  onResult(text: string): void;
  onStateChange(recording: boolean): void;
  onError(code: string): void;
}

export interface Voice {
  readonly supported: boolean;
  setLang(lang: string): void;
  toggle(): void;
  stop(): void;
  destroy(): void;
}

export function createVoice(lang: string, handlers: VoiceHandlers): Voice {
  const scope = window as unknown as { SpeechRecognition?: SpeechCtor; webkitSpeechRecognition?: SpeechCtor };
  const Impl = scope.SpeechRecognition || scope.webkitSpeechRecognition;

  if (!Impl) {
    return {
      supported: false,
      setLang: () => {},
      toggle: () => {},
      stop: () => {},
      destroy: () => {},
    };
  }

  const recognition = new Impl();
  recognition.lang = lang;
  recognition.continuous = true;
  recognition.interimResults = true;

  let recording = false;

  recognition.onresult = (event) => {
    let text = "";
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      if (result) text += result[0].transcript;
    }
    handlers.onResult(text);
  };
  recognition.onend = () => {
    recording = false;
    handlers.onStateChange(false);
  };
  recognition.onerror = (event) => {
    recording = false;
    handlers.onStateChange(false);
    /* "aborted" và "no-speech" là chuyện thường ngày, không phải lỗi để báo. */
    if (event.error && event.error !== "aborted" && event.error !== "no-speech") {
      handlers.onError(event.error);
    }
  };

  return {
    supported: true,
    setLang(next: string) {
      recognition.lang = next;
    },
    toggle() {
      if (recording) {
        recognition.stop();
        return;
      }
      handlers.onStart();
      try {
        recognition.start();
        recording = true;
        handlers.onStateChange(true);
      } catch {
        /* start() ném nếu đang chạy sẵn — bỏ qua. */
      }
    },
    stop() {
      if (recording) recognition.stop();
    },
    destroy() {
      recognition.onresult = null;
      recognition.onend = null;
      recognition.onerror = null;
      try {
        recognition.abort();
      } catch {
        /* chưa từng start */
      }
    },
  };
}

/* --------------------------------------------------------------------------
   Ghi âm + nhận dạng ở SERVER (server/stt.mjs). MediaRecorder có trên Chrome, Edge, Safari 14.1+ và FIREFOX,
   nên đây là đường dùng khi server bật nhận giọng nói. Bấm micro = bắt đầu nghe, bấm lần nữa = gửi đi nhận dạng
   (tự dừng sau `maxMs`). Chữ nhận được điền vào ô nhập — người dùng xem lại rồi mới gửi.
   -------------------------------------------------------------------------- */

export interface RecorderHandlers extends VoiceHandlers {
  /** Gửi đoạn ghi âm đi nhận dạng, trả chữ. */
  transcribe(audio: Blob): Promise<string>;
  onPhase(phase: "idle" | "listening" | "transcribing"): void;
}

interface RecorderLike {
  state: string;
  mimeType: string;
  start(timeslice?: number): void;
  stop(): void;
  ondataavailable: ((e: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
}
type RecorderCtor = (new (stream: MediaStream, opts?: { mimeType?: string; audioBitsPerSecond?: number }) => RecorderLike) & { isTypeSupported?(t: string): boolean };

/** Định dạng ghi âm tốt nhất trình duyệt có: Chrome/Edge → webm/opus, Firefox → ogg/opus, Safari → mp4. */
export function pickAudioType(Rec: { isTypeSupported?(t: string): boolean } | undefined): string {
  const order = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/webm", "audio/ogg", "audio/mp4"];
  if (!Rec?.isTypeSupported) return "";
  return order.find((t) => Rec.isTypeSupported!(t)) || "";
}

export function recorderSupported(): boolean {
  const w = window as unknown as { MediaRecorder?: RecorderCtor };
  return typeof w.MediaRecorder === "function" && !!navigator.mediaDevices?.getUserMedia;
}

export function createRecorderVoice(handlers: RecorderHandlers, maxMs = 90_000): Voice {
  const MaybeRec = (window as unknown as { MediaRecorder?: RecorderCtor }).MediaRecorder;
  if (!MaybeRec || !navigator.mediaDevices?.getUserMedia) {
    return { supported: false, setLang: () => {}, toggle: () => {}, stop: () => {}, destroy: () => {} };
  }
  const Rec: RecorderCtor = MaybeRec;
  let rec: RecorderLike | null = null;
  let stream: MediaStream | null = null;
  let chunks: Blob[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let busy = false;
  let dead = false;
  /* đo âm lượng khi ghi: gần như im lặng suốt (bấm nhầm, micro tắt tiếng) ⇒ ⛔ gửi server — mô hình gặp im lặng
     hay "đoán" ra chữ (đo thật 2026-10-21) */
  let meter: { ctx: AudioContext; timer: ReturnType<typeof setInterval> } | null = null;
  let peak = 0;
  const SILENCE_RMS = 0.012;
  /* TỰ DỪNG (owner 2026-10-21 "tự dừng + tự gửi"): đã có tiếng nói rồi im `endSilenceMs` ⇒ dừng; mở mic mà
     `noSpeechMs` chưa nói gì ⇒ dừng (báo "chưa nhận dạng được"). Ngưỡng tự chỉnh theo tiếng ồn nền đo 0,3 giây đầu. */
  const endSilenceMs = 1400;
  const noSpeechMs = 7000;

  const release = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (meter) {
      clearInterval(meter.timer);
      void meter.ctx.close().catch(() => {});
      meter = null;
    }
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
  };

  async function start() {
    busy = true;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch {
      busy = false;
      handlers.onError("not-allowed");
      return;
    }
    if (dead) return release();
    const mimeType = pickAudioType(Rec);
    rec = new Rec(stream, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 32000 });
    chunks = [];
    peak = 0;
    try {
      const AC = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const buf = new Float32Array(analyser.fftSize);
        const t0 = Date.now();
        let floor = Infinity;
        let spoke = false;
        let lastLoud = t0;
        meter = {
          ctx,
          timer: setInterval(() => {
            analyser.getFloatTimeDomainData(buf);
            let sum = 0;
            for (const v of buf) sum += v * v;
            const rms = Math.sqrt(sum / buf.length);
            peak = Math.max(peak, rms);
            const now = Date.now();
            if (now - t0 < 300) {
              floor = Math.min(floor, rms);
              return;
            }
            /* trần 0,03: người dùng NÓI NGAY khi bấm thì 0,3 giây đầu đã là tiếng nói — ⛔ coi đó là tiếng ồn nền
               (đo thật 2026-10-21: không trần ⇒ ⛔ bao giờ thấy "đã nói", phải chờ đủ 7 giây) */
            const base = Number.isFinite(floor) ? Math.min(floor, 0.03) : 0;
            if (rms > Math.max(0.02, base * 2.5)) {
              spoke = true;
              lastLoud = now;
            }
            const done = spoke ? now - lastLoud > endSilenceMs : now - t0 > noSpeechMs;
            if (done && rec?.state === "recording") rec.stop();
          }, 100),
        };
      } else peak = 1;
    } catch {
      peak = 1; /* không đo được ⇒ cứ gửi, như trước */
    }
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size) chunks.push(e.data);
    };
    rec.onstop = () => void finish();
    handlers.onStart();
    rec.start(1000);
    busy = false;
    handlers.onStateChange(true);
    handlers.onPhase("listening");
    timer = setTimeout(() => rec?.state === "recording" && rec.stop(), maxMs);
  }

  async function finish() {
    const type = (rec?.mimeType || pickAudioType(Rec) || "audio/webm").split(";")[0] || "audio/webm";
    release();
    rec = null;
    handlers.onStateChange(false);
    if (dead) return;
    const audio = new Blob(chunks, { type });
    chunks = [];
    if (audio.size < 2000 || peak < SILENCE_RMS) {
      if (audio.size >= 2000) {
        handlers.onPhase("idle");
        handlers.onError("no-speech");
        return;
      }
      /* bấm nhầm / chưa kịp nói: ⛔ đốt một lượt gọi mô hình */
      handlers.onPhase("idle");
      return;
    }
    busy = true;
    handlers.onPhase("transcribing");
    let text = "";
    let error: string | null = null;
    try {
      text = (await handlers.transcribe(audio)).trim();
    } catch (e) {
      error = e instanceof Error && e.message ? "server:" + e.message : "server";
    } finally {
      busy = false;
    }
    if (dead) return;
    /* về "idle" TRƯỚC, rồi mới báo: báo lỗi hiện ở placeholder, "idle" sau đó sẽ xoá mất (đo thật 2026-10-21) */
    handlers.onPhase("idle");
    if (error) handlers.onError(error);
    else if (text) handlers.onResult(text);
    else handlers.onError("no-speech");
  }

  return {
    supported: true,
    setLang: () => {},
    toggle() {
      if (busy || dead) return;
      if (rec?.state === "recording") rec.stop();
      else void start();
    },
    stop() {
      if (rec?.state === "recording") rec.stop();
    },
    destroy() {
      dead = true;
      if (rec?.state === "recording") rec.stop();
      release();
    },
  };
}
